import { useState, useCallback, useEffect, type ReactNode } from "react";
import { AuthContext, type ClinicUser } from "@/hooks/auth-context";

const STORAGE_KEY = "qp_clinic_auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [clinicUser, setClinicUser] = useState<ClinicUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ClinicUser;
        setClinicUser(parsed);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    setIsLoading(false);
  }, []);

  const persist = useCallback((user: ClinicUser) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    setClinicUser(user);
  }, []);

  const login = useCallback(
    async (email: string, _password: string) => {
      // Mock auth — accept any credentials
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 800));

      // If we have stored user info from signup, restore it
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setClinicUser(JSON.parse(stored) as ClinicUser);
        return;
      }

      // Fallback — create minimal user from email
      const user: ClinicUser = {
        email,
        clinicName: "Returning Clinic",
        leadProsthetist: "",
      };
      persist(user);
    },
    [persist],
  );

  const signup = useCallback(
    async (user: ClinicUser, _password: string) => {
      // Mock signup — simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1000));
      persist(user);
    },
    [persist],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setClinicUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        clinicUser,
        isAuthenticated: !!clinicUser,
        isLoading,
        login,
        signup,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
